/**
 * Sweep Control (R27) — data access. Both calls are Supabase Edge Function
 * invocations (server-side authorized: requireAdmin), so no client writes and
 * the client-write gate stays green.
 */

import { supabase } from "@/auth/supabase-client";
import {
  ensureFreshSession,
  SUPABASE_EDGE_TIMEOUT_MS,
  withSupabaseTimeout,
} from "@/auth/session-guard";
import type { RawSweepHealth, RawTriggerResult } from "./types";

/** Best-effort extraction of the Edge Function's error message. */
async function messageFor(error: unknown): Promise<string> {
  const ctx = (error as { context?: Response }).context;
  if (ctx && typeof ctx.json === "function") {
    try {
      const parsed = (await ctx.json()) as {
        error?: { message?: string } | string;
        message?: string;
      };
      if (parsed && typeof parsed.error === "object" && parsed.error?.message) {
        return parsed.error.message;
      }
      if (parsed && typeof parsed.message === "string") return parsed.message;
    } catch {
      /* non-JSON error body — fall through to the generic message */
    }
  }
  return error instanceof Error ? error.message : String(error);
}

async function invoke<T>(name: string, body: Record<string, unknown> = {}): Promise<T> {
  await ensureFreshSession();
  const { data, error } = await withSupabaseTimeout(
    supabase.functions.invoke(name, { body }),
    `${name} request`,
    SUPABASE_EDGE_TIMEOUT_MS,
  );
  if (error) throw new Error(`${name}: ${await messageFor(error)}`);
  return data as T;
}

/** trigger_manual_sweep — dispatch a manual sweep (admin/super_admin, enforced
 *  server-side; fire-and-return via a durable queue). The RPC envelope's status
 *  is "started" (dispatched) or "run_in_progress" (run-lock held). */
export async function triggerManualSweep(): Promise<RawTriggerResult> {
  const res = await invoke<{ data: RawTriggerResult }>("trigger_manual_sweep", {});
  return res.data;
}

/** get_sweep_health — the last-N sweep runs with classification (backend default
 *  N=10; invoke posts no query param, so the default applies). */
export async function fetchSweepHealth(): Promise<RawSweepHealth> {
  const res = await invoke<{ data: RawSweepHealth }>("get_sweep_health", {});
  return res.data;
}
